import { ReactNode, useContext } from "react";
import { Avatar, Button, CircularProgress, Hidden, Paper, Theme, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { observer } from "mobx-react-lite";
import LanguageContext from "context/LanguageContext";
import { PlanetImage } from "images";
import ReviewContext from "context/ReviewContext";

const useStyles = makeStyles()((theme: Theme) => ({
	paper: {
		position: "relative",
		border: `1px solid ${theme.palette.divider}`,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		padding: theme.spacing(2, 4),
		marginInline: theme.spacing(2),
	},
	avatar: {
		width: theme.spacing(8),
		height: theme.spacing(8),
		marginBottom: theme.spacing(3),
	},
	imageStart: {
		transform: "rotate(180deg)",
		position: "absolute",
		top: theme.spacing(1),
		left: theme.spacing(-24),
		zIndex: -1,
	},
	imageEnd: {
		position: "absolute",
		bottom: theme.spacing(-10.5),
		right: theme.spacing(-24),
		zIndex: -1,
	},
	subTitle: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1.5),
	},
	button: {
		width: theme.spacing(35),
		padding: theme.spacing(2, 0),
		lineHeight: "100%",
		[theme.breakpoints.down("sm")]: {
			width: theme.spacing(25),
		},
	},
	submit: {
		marginBottom: theme.spacing(2),
	},
	textCenter: {
		textAlign: "center",
		whiteSpace: "pre-wrap",
	},
}));

interface IProps {
	title?: string;
	subTitle?: string;
	onSubmit?: () => void;
	onCancel?: () => void;
	submitTitle?: string;
	cancelTitle?: string;
	hideStartImage?: boolean;
	hideAvatar?: boolean;
	className?: string;
	isLoading?: boolean;
	children?: ReactNode;
	disabledSubmit?: boolean;
}

export const StepCard = observer(
	({
		children,
		title,
		subTitle,
		onSubmit,
		onCancel,
		submitTitle,
		cancelTitle,
		hideStartImage,
		hideAvatar,
		className,
		isLoading,
		disabledSubmit,
	}: IProps) => {
		const { classes, cx } = useStyles();
		const { getTranslate } = useContext(LanguageContext);
		const { location } = useContext(ReviewContext);

		const mediaUrl =
			location?.media?.find(item => item.category_code === "LOGO")?.url || location?.media[0]?.url;

		return (
			<Paper elevation={0} className={cx(classes.paper, className)} data-testid={title}>
				{!hideStartImage && (
					<Hidden smDown>
						<PlanetImage className={classes.imageStart} />
					</Hidden>
				)}
				{!hideAvatar && mediaUrl && <Avatar className={classes.avatar} src={mediaUrl} />}
				<Typography variant="h1" className={classes.textCenter} data-lang={title}>
					{getTranslate(title)}
				</Typography>
				<Typography className={cx(classes.subTitle, classes.textCenter)} data-lang={subTitle}>
					{getTranslate(subTitle)}
				</Typography>
				{children}
				{onSubmit && submitTitle && (
					<Button
						color="primary"
						variant="contained"
						className={cx(classes.button, classes.submit)}
						onClick={onSubmit}
						data-lang={submitTitle}
						data-testid={submitTitle}
						disabled={isLoading || disabledSubmit}
						startIcon={isLoading ? <CircularProgress size={15} /> : undefined}
					>
						{getTranslate(submitTitle)}
					</Button>
				)}
				{onCancel && cancelTitle && (
					<Button
						color="primary"
						className={classes.button}
						onClick={onCancel}
						data-lang={cancelTitle}
						data-testid={cancelTitle}
						disabled={isLoading}
					>
						{getTranslate(cancelTitle)}
					</Button>
				)}
				<Hidden smDown>
					<PlanetImage className={classes.imageEnd} />
				</Hidden>
			</Paper>
		);
	},
);
